import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, Pressable, ScrollView, TextInput } from '../lib/react-native-compat';

const COMM_API = 'http://localhost:3101/api/comms';

const COLORS = {
    navy: '#173659',
    red: '#dc2626',
    green: '#16a34a',
    blue: '#2563eb',
    amber: '#f59e0b',
    grey: '#64748b',
    border: '#e2e8f0',
    bg: '#f8fafc',
    cardBg: '#ffffff',
};

const Badge = ({ color, label }) => (
    <View style={{ backgroundColor: color + '18', borderRadius: 12, paddingHorizontal: 10, paddingVertical: 3 }}>
        <Text style={{ color, fontSize: 11, fontWeight: '600' }}>{label}</Text>
    </View>
);

const StatBox = ({ label, value, color }) => (
    <View style={{ backgroundColor: COLORS.cardBg, borderRadius: 10, padding: 16, minWidth: 130, borderWidth: 1, borderColor: COLORS.border, alignItems: 'center' }}>
        <Text style={{ fontSize: 28, fontWeight: '700', color: color || COLORS.navy }}>{value}</Text>
        <Text style={{ fontSize: 11, color: COLORS.grey, marginTop: 4 }}>{label}</Text>
    </View>
);

const TOOLS = [
    {
        id: 'test-pcard-anomaly',
        name: 'Test P-Card Anomaly',
        description: 'Queues a sample P-Card anomaly notification (unusual spending pattern) to the test recipient for preview & approval.',
        method: 'POST',
        endpoint: '/test/pcard-anomaly',
        category: 'test',
        icon: '💳',
    },
    {
        id: 'test-stale-forecast',
        name: 'Test Stale Forecast',
        description: 'Queues a sample stale forecast notification — warns a PM their forecast hasn\'t been updated recently.',
        method: 'POST',
        endpoint: '/test/stale-forecast',
        category: 'test',
        icon: '📊',
    },
    {
        id: 'test-weekly-digest',
        name: 'Test Weekly Digest',
        description: 'Queues a sample weekly digest email summarizing the week\'s flagged items, stats, and action items.',
        method: 'POST',
        endpoint: '/test/weekly-digest',
        category: 'test',
        icon: '📰',
    },
    {
        id: 'test-send-email',
        name: 'Send Test Email',
        description: 'Sends a test email directly (bypasses approval queue) to the configured test recipient. Verifies SMTP connectivity.',
        method: 'POST',
        endpoint: '/test/send-email',
        category: 'test',
        icon: '✉️',
    },
    {
        id: 'approve-all',
        name: 'Approve All Pending',
        description: 'Approves and sends all messages currently waiting in the approval queue. Use after reviewing previews.',
        method: 'POST',
        endpoint: '/approve-all',
        category: 'action',
        icon: '✅',
    },
    {
        id: 'view-pending',
        name: 'View Pending Queue',
        description: 'Shows all messages waiting for approval. Each message can be individually approved or rejected.',
        method: 'GET',
        endpoint: '/pending',
        category: 'view',
        icon: '📋',
    },
    {
        id: 'view-stats',
        name: 'View Stats',
        description: 'Dashboard statistics — total messages, breakdown by status/channel, last 24h activity, Teams config status.',
        method: 'GET',
        endpoint: '/stats',
        category: 'view',
        icon: '📈',
    },
    {
        id: 'view-templates',
        name: 'View Templates',
        description: 'Lists all available email/Teams message templates with their IDs and descriptions.',
        method: 'GET',
        endpoint: '/templates',
        category: 'view',
        icon: '🎨',
    },
    {
        id: 'view-queue',
        name: 'View Full Queue',
        description: 'Shows the entire communication log — all messages regardless of status (sent, pending, rejected, etc).',
        method: 'GET',
        endpoint: '/queue',
        category: 'view',
        icon: '📜',
    },
];

const CommunicationTools = () => {
    const [results, setResults] = useState({});
    const [loading, setLoading] = useState({});
    const [stats, setStats] = useState(null);
    const [pending, setPending] = useState([]);
    const [filter, setFilter] = useState('all');
    const [previewHtml, setPreviewHtml] = useState(null);

    // Load stats on mount
    useEffect(() => {
        fetchStats();
        fetchPending();
    }, []);

    const fetchStats = useCallback(async () => {
        try {
            const res = await fetch(`${COMM_API}/stats`);
            const data = await res.json();
            setStats(data);
        } catch (e) {
            console.error('Failed to load comm stats:', e);
        }
    }, []);

    const fetchPending = useCallback(async () => {
        try {
            const res = await fetch(`${COMM_API}/pending`);
            const data = await res.json();
            setPending(data);
        } catch (e) {
            console.error('Failed to load pending:', e);
        }
    }, []);

    const runTool = useCallback(async (tool) => {
        setLoading(prev => ({ ...prev, [tool.id]: true }));
        setResults(prev => ({ ...prev, [tool.id]: null }));
        try {
            const opts = tool.method === 'POST'
                ? { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }
                : { method: 'GET' };
            const res = await fetch(`${COMM_API}${tool.endpoint}`, opts);
            const data = await res.json();
            setResults(prev => ({ ...prev, [tool.id]: { ok: res.ok, data } }));
            // Refresh stats & pending after actions
            fetchStats();
            fetchPending();
        } catch (e) {
            setResults(prev => ({ ...prev, [tool.id]: { ok: false, data: { error: e.message } } }));
        } finally {
            setLoading(prev => ({ ...prev, [tool.id]: false }));
        }
    }, [fetchStats, fetchPending]);

    const approveSingle = useCallback(async (id) => {
        try {
            await fetch(`${COMM_API}/approve/${encodeURIComponent(id)}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ approvedBy: 'dashboard-ui' }),
            });
            fetchPending();
            fetchStats();
        } catch (e) {
            console.error('Approve failed:', e);
        }
    }, [fetchPending, fetchStats]);

    const rejectSingle = useCallback(async (id) => {
        try {
            await fetch(`${COMM_API}/reject/${encodeURIComponent(id)}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ rejectedBy: 'dashboard-ui' }),
            });
            fetchPending();
            fetchStats();
        } catch (e) {
            console.error('Reject failed:', e);
        }
    }, [fetchPending, fetchStats]);

    const loadPreview = useCallback(async (id) => {
        try {
            const res = await fetch(`${COMM_API}/preview/${encodeURIComponent(id)}`);
            const html = await res.text();
            setPreviewHtml(html);
        } catch (e) {
            setPreviewHtml('<p style="color:red;">Failed to load preview</p>');
        }
    }, []);

    const filtered = filter === 'all' ? TOOLS : TOOLS.filter(t => t.category === filter);

    return (
        <ScrollView style={{ flex: 1, backgroundColor: COLORS.bg, padding: 24 }}>
            {/* Header */}
            <View style={{ marginBottom: 24 }}>
                <Text style={{ fontSize: 22, fontWeight: '700', color: COLORS.navy }}>
                    Communication Tools
                </Text>
                <Text style={{ fontSize: 13, color: COLORS.grey, marginTop: 4 }}>
                    AI notification system — queue, preview, approve, and send emails & Teams messages from rule-based triggers.
                </Text>
            </View>

            {/* Stats Row */}
            {stats && (
                <View style={{ flexDirection: 'row', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
                    <StatBox label="Total Messages" value={stats.total} />
                    <StatBox label="Pending" value={stats.byStatus?.PENDING || 0} color={COLORS.amber} />
                    <StatBox label="Sent" value={stats.byStatus?.SENT || 0} color={COLORS.green} />
                    <StatBox label="Rejected" value={stats.byStatus?.REJECTED || 0} color={COLORS.red} />
                    <StatBox label="Last 24h" value={stats.last24h} color={COLORS.blue} />
                    <StatBox label="Teams" value={stats.teamsConfigured ? 'ON' : 'OFF'} color={stats.teamsConfigured ? COLORS.green : COLORS.grey} />
                </View>
            )}

            {/* Pending Queue */}
            {pending.length > 0 && (
                <View style={{ backgroundColor: COLORS.cardBg, borderRadius: 10, padding: 16, marginBottom: 24, borderWidth: 1, borderColor: COLORS.amber + '40' }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 12 }}>
                        <Text style={{ fontSize: 15, fontWeight: '600', color: COLORS.navy }}>
                            ⏳ Pending Approval ({pending.length})
                        </Text>
                        <Pressable
                            onPress={() => runTool(TOOLS.find(t => t.id === 'approve-all'))}
                            style={{ marginLeft: 'auto', backgroundColor: COLORS.green, borderRadius: 6, paddingHorizontal: 12, paddingVertical: 5 }}
                        >
                            <Text style={{ color: '#fff', fontSize: 12, fontWeight: '600' }}>Approve All</Text>
                        </Pressable>
                    </View>
                    {pending.map((msg, idx) => (
                        <View key={msg.id || idx} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 8, borderTopWidth: idx > 0 ? 1 : 0, borderColor: COLORS.border }}>
                            <View style={{ flex: 1 }}>
                                <Text style={{ fontSize: 13, fontWeight: '600', color: COLORS.navy }}>{msg.subject || msg.templateId || 'Message'}</Text>
                                <Text style={{ fontSize: 11, color: COLORS.grey }}>
                                    To: {msg.recipientEmail || '—'} · {msg.channel} · {new Date(msg.createdAt).toLocaleString()}
                                </Text>
                            </View>
                            <Pressable
                                onPress={() => loadPreview(msg.id)}
                                style={{ backgroundColor: COLORS.blue + '15', borderRadius: 5, paddingHorizontal: 8, paddingVertical: 4, marginRight: 6 }}
                            >
                                <Text style={{ color: COLORS.blue, fontSize: 11, fontWeight: '600' }}>Preview</Text>
                            </Pressable>
                            <Pressable
                                onPress={() => approveSingle(msg.id)}
                                style={{ backgroundColor: COLORS.green + '15', borderRadius: 5, paddingHorizontal: 8, paddingVertical: 4, marginRight: 6 }}
                            >
                                <Text style={{ color: COLORS.green, fontSize: 11, fontWeight: '600' }}>Approve</Text>
                            </Pressable>
                            <Pressable
                                onPress={() => rejectSingle(msg.id)}
                                style={{ backgroundColor: COLORS.red + '15', borderRadius: 5, paddingHorizontal: 8, paddingVertical: 4 }}
                            >
                                <Text style={{ color: COLORS.red, fontSize: 11, fontWeight: '600' }}>Reject</Text>
                            </Pressable>
                        </View>
                    ))}
                </View>
            )}

            {/* Preview Modal */}
            {previewHtml && (
                <View style={{ backgroundColor: COLORS.cardBg, borderRadius: 10, padding: 16, marginBottom: 24, borderWidth: 2, borderColor: COLORS.blue }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 12 }}>
                        <Text style={{ fontSize: 15, fontWeight: '600', color: COLORS.navy }}>Email Preview</Text>
                        <Pressable
                            onPress={() => setPreviewHtml(null)}
                            style={{ marginLeft: 'auto', backgroundColor: COLORS.grey + '20', borderRadius: 5, paddingHorizontal: 10, paddingVertical: 4 }}
                        >
                            <Text style={{ color: COLORS.grey, fontSize: 12, fontWeight: '600' }}>Close</Text>
                        </Pressable>
                    </View>
                    <iframe
                        srcDoc={previewHtml}
                        style={{ width: '100%', height: 500, border: 'none', borderRadius: 8, backgroundColor: '#fff' }}
                        title="Email Preview"
                        sandbox="allow-same-origin"
                    />
                </View>
            )}

            {/* Filter Tabs */}
            <View style={{ flexDirection: 'row', gap: 8, marginBottom: 16 }}>
                {[
                    { key: 'all', label: 'All Tools' },
                    { key: 'test', label: 'Test & Queue' },
                    { key: 'action', label: 'Actions' },
                    { key: 'view', label: 'View Data' },
                ].map(f => (
                    <Pressable
                        key={f.key}
                        onPress={() => setFilter(f.key)}
                        style={{
                            paddingHorizontal: 14,
                            paddingVertical: 6,
                            borderRadius: 20,
                            backgroundColor: filter === f.key ? COLORS.navy : COLORS.cardBg,
                            borderWidth: 1,
                            borderColor: filter === f.key ? COLORS.navy : COLORS.border,
                        }}
                    >
                        <Text style={{ fontSize: 12, fontWeight: '600', color: filter === f.key ? '#fff' : COLORS.grey }}>
                            {f.label}
                        </Text>
                    </Pressable>
                ))}
            </View>

            {/* Tool Cards */}
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 16, marginBottom: 40 }}>
                {filtered.map(tool => {
                    const result = results[tool.id];
                    const isLoading = loading[tool.id];
                    return (
                        <View
                            key={tool.id}
                            style={{
                                backgroundColor: COLORS.cardBg,
                                borderRadius: 10,
                                padding: 18,
                                width: 320,
                                borderWidth: 1,
                                borderColor: result?.ok === false ? COLORS.red + '40' : result?.ok ? COLORS.green + '40' : COLORS.border,
                                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                            }}
                        >
                            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                                <Text style={{ fontSize: 20, marginRight: 8 }}>{tool.icon}</Text>
                                <Text style={{ fontSize: 14, fontWeight: '700', color: COLORS.navy, flex: 1 }}>{tool.name}</Text>
                                <Badge
                                    color={tool.category === 'test' ? COLORS.blue : tool.category === 'action' ? COLORS.amber : COLORS.grey}
                                    label={tool.category}
                                />
                            </View>
                            <Text style={{ fontSize: 12, color: COLORS.grey, lineHeight: 18, marginBottom: 12 }}>
                                {tool.description}
                            </Text>
                            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                <Pressable
                                    onPress={() => runTool(tool)}
                                    disabled={isLoading}
                                    style={{
                                        backgroundColor: isLoading ? COLORS.grey : COLORS.navy,
                                        borderRadius: 6,
                                        paddingHorizontal: 16,
                                        paddingVertical: 7,
                                        opacity: isLoading ? 0.6 : 1,
                                    }}
                                >
                                    <Text style={{ color: '#fff', fontSize: 12, fontWeight: '600' }}>
                                        {isLoading ? 'Running...' : tool.method === 'GET' ? 'Fetch' : 'Run'}
                                    </Text>
                                </Pressable>
                                <Text style={{ fontSize: 10, color: COLORS.grey, marginLeft: 8, fontFamily: 'monospace' }}>
                                    {tool.method} {tool.endpoint}
                                </Text>
                            </View>

                            {/* Result */}
                            {result && (
                                <View style={{
                                    marginTop: 10,
                                    backgroundColor: result.ok ? COLORS.green + '08' : COLORS.red + '08',
                                    borderRadius: 6,
                                    padding: 10,
                                    maxHeight: 200,
                                    overflow: 'auto',
                                }}>
                                    <Text style={{
                                        fontSize: 11,
                                        fontFamily: 'monospace',
                                        color: result.ok ? COLORS.green : COLORS.red,
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-all',
                                    }}>
                                        {JSON.stringify(result.data, null, 2)}
                                    </Text>
                                </View>
                            )}
                        </View>
                    );
                })}
            </View>

            {/* Open Preview Server Link */}
            <View style={{ backgroundColor: COLORS.cardBg, borderRadius: 10, padding: 16, marginBottom: 40, borderWidth: 1, borderColor: COLORS.border }}>
                <Text style={{ fontSize: 14, fontWeight: '600', color: COLORS.navy, marginBottom: 6 }}>
                    Full Preview Dashboard
                </Text>
                <Text style={{ fontSize: 12, color: COLORS.grey, marginBottom: 10 }}>
                    For full-featured email previews with approve/reject workflow, open the dedicated preview server.
                </Text>
                <Pressable
                    onPress={() => window.open('http://localhost:3102', '_blank')}
                    style={{ backgroundColor: COLORS.blue, borderRadius: 6, paddingHorizontal: 16, paddingVertical: 7, alignSelf: 'flex-start' }}
                >
                    <Text style={{ color: '#fff', fontSize: 12, fontWeight: '600' }}>Open Preview Server (port 3102)</Text>
                </Pressable>
            </View>
        </ScrollView>
    );
};

export default CommunicationTools;
